const BASE_PATH = "http://localhost".replace(/\/+$/, "");
const isBlob = (value) => typeof Blob !== "undefined" && value instanceof Blob;
class BaseAPI {
  constructor(configuration = new Configuration()) {
    this.configuration = configuration;
    this.fetchApi = async (url, init) => {
      let fetchParams = { url, init };
      for (const middleware of this.middleware) {
        if (middleware.pre) {
          fetchParams = await middleware.pre({
            fetch: this.fetchApi,
            ...fetchParams
          }) || fetchParams;
        }
      }
      let response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
      for (const middleware of this.middleware) {
        if (middleware.post) {
          response = await middleware.post({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            response: response.clone()
          }) || response;
        }
      }
      return response;
    };
    this.middleware = configuration.middleware;
  }
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  async request(context, initOverrides) {
    const { url, init } = this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    throw response;
  }
  createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const body = typeof FormData !== "undefined" && context.body instanceof FormData || context.body instanceof URLSearchParams || isBlob(context.body) ? context.body : JSON.stringify(context.body);
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    const init = {
      method: context.method,
      headers,
      body,
      credentials: this.configuration.credentials,
      ...initOverrides
    };
    return { url, init };
  }
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
}
class RequiredError extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
    this.name = "RequiredError";
  }
}
const COLLECTION_FORMATS = {
  csv: ",",
  ssv: " ",
  tsv: "	",
  pipes: "|"
};
class Configuration {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
}
function exists(json, key) {
  const value = json[key];
  return value !== null && value !== void 0;
}
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => {
    const fullKey = prefix + (prefix.length ? `[${key}]` : key);
    const value = params[key];
    if (value instanceof Array) {
      const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
      return `${encodeURIComponent(fullKey)}=${multiValue}`;
    }
    if (value instanceof Date) {
      return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
    }
    if (value instanceof Object) {
      return querystring(value, fullKey);
    }
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
  }).filter((part) => part.length > 0).join("&");
}
function mapValues(data, fn) {
  return Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: fn(data[key]) }), {});
}
function canConsumeForm(consumes) {
  for (const consume of consumes) {
    if (consume.contentType === "multipart/form-data") {
      return true;
    }
  }
  return false;
}
class JSONApiResponse {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
}
class VoidApiResponse {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return void 0;
  }
}
class BlobApiResponse {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.blob();
  }
}
class TextApiResponse {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
}

class AddressesApi extends BaseAPI {
  async apiV2AddressesAddressIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.addressId === null || requestParameters.addressId === void 0) {
      throw new RequiredError("addressId", "Required parameter requestParameters.addressId was null or undefined when calling apiV2AddressesAddressIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Addresses/{addressId}`.replace(`{${"addressId"}}`, encodeURIComponent(String(requestParameters.addressId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AddressesAddressIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2AddressesAddressIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AddressesOrganizationIdAddressIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2AddressesOrganizationIdAddressIdGet.");
    }
    if (requestParameters.addressId === null || requestParameters.addressId === void 0) {
      throw new RequiredError("addressId", "Required parameter requestParameters.addressId was null or undefined when calling apiV2AddressesOrganizationIdAddressIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.copyPulsarLogic !== void 0) {
      queryParameters["copyPulsarLogic"] = requestParameters.copyPulsarLogic;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Addresses/{organizationId}/{addressId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"addressId"}}`, encodeURIComponent(String(requestParameters.addressId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AddressesOrganizationIdAddressIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2AddressesOrganizationIdAddressIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class AuctionAccessApi extends BaseAPI {
  async apiV2AuctionAccessCompanyActivityRequestPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/CompanyActivityRequest`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.companyActivityRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2AuctionAccessCompanyActivityRequestPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuctionAccessCompanyActivityRequestPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessFloorPlanRequestPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/FloorPlanRequest`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.floorPlanRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2AuctionAccessFloorPlanRequestPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuctionAccessFloorPlanRequestPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessFlooringTitleUpdateRequestPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/FlooringTitleUpdateRequest`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.flooringTitleUpdateRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2AuctionAccessFlooringTitleUpdateRequestPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuctionAccessFlooringTitleUpdateRequestPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessIssueRequestPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/IssueRequest`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.issueRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2AuctionAccessIssueRequestPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuctionAccessIssueRequestPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessRequestInformationPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/RequestInformation`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.informationRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2AuctionAccessRequestInformationPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuctionAccessRequestInformationPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class AuditsApi extends BaseAPI {
  async apiV2AuditsCommonPageNumberPageSizeGetRaw(requestParameters, initOverrides) {
    if (requestParameters.pageNumber === null || requestParameters.pageNumber === void 0) {
      throw new RequiredError("pageNumber", "Required parameter requestParameters.pageNumber was null or undefined when calling apiV2AuditsCommonPageNumberPageSizeGet.");
    }
    if (requestParameters.pageSize === null || requestParameters.pageSize === void 0) {
      throw new RequiredError("pageSize", "Required parameter requestParameters.pageSize was null or undefined when calling apiV2AuditsCommonPageNumberPageSizeGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Audits/common/{pageNumber}/{pageSize}`.replace(`{${"pageNumber"}}`, encodeURIComponent(String(requestParameters.pageNumber))).replace(`{${"pageSize"}}`, encodeURIComponent(String(requestParameters.pageSize))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.auditEntriesRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuditsCommonPageNumberPageSizeGet(requestParameters, initOverrides) {
    const response = await this.apiV2AuditsCommonPageNumberPageSizeGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuditsRegistrationPageNumberPageSizeGetRaw(requestParameters, initOverrides) {
    if (requestParameters.pageNumber === null || requestParameters.pageNumber === void 0) {
      throw new RequiredError("pageNumber", "Required parameter requestParameters.pageNumber was null or undefined when calling apiV2AuditsRegistrationPageNumberPageSizeGet.");
    }
    if (requestParameters.pageSize === null || requestParameters.pageSize === void 0) {
      throw new RequiredError("pageSize", "Required parameter requestParameters.pageSize was null or undefined when calling apiV2AuditsRegistrationPageNumberPageSizeGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Audits/registration/{pageNumber}/{pageSize}`.replace(`{${"pageNumber"}}`, encodeURIComponent(String(requestParameters.pageNumber))).replace(`{${"pageSize"}}`, encodeURIComponent(String(requestParameters.pageSize))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.auditEntriesRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuditsRegistrationPageNumberPageSizeGet(requestParameters, initOverrides) {
    const response = await this.apiV2AuditsRegistrationPageNumberPageSizeGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class AuthorizationApi extends BaseAPI {
  async apiV2AuthorizationCheckPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.schemaId !== void 0) {
      queryParameters["schemaId"] = requestParameters.schemaId;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Authorization/Check`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.authorizationCheckRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2AuthorizationCheckPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuthorizationCheckPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuthorizationObjectsPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.schemaId !== void 0) {
      queryParameters["schemaId"] = requestParameters.schemaId;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Authorization/Objects`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.authorizationListObjectsRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuthorizationObjectsPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuthorizationObjectsPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuthorizationRelationsPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.schemaId !== void 0) {
      queryParameters["schemaId"] = requestParameters.schemaId;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Authorization/Relations`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.authorizationListRelationsRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuthorizationRelationsPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2AuthorizationRelationsPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class CheckoutPreferencesApi extends BaseAPI {
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesActivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesActivateCheckoutPreferencesIdPut.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesActivateCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/Activate/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesActivateCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesActivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdGet.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdPut.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateCheckoutPreferencesRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCurrentGetRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCurrentGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/Current`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCurrentGet(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesCurrentGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeactivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeactivateCheckoutPreferencesIdPut.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeactivateCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/Deactivate/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeactivateCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeactivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeleteCheckoutPreferencesIdDeleteRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeleteCheckoutPreferencesIdDelete.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeleteCheckoutPreferencesIdDelete.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/Delete/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeleteCheckoutPreferencesIdDelete(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDeleteCheckoutPreferencesIdDeleteRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDisableCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDisableCheckoutPreferencesIdPut.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDisableCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/Disable/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDisableCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesDisableCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentMethodsRemoveCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentMethodsRemoveCheckoutPreferencesIdPut.");
    }
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentMethodsRemoveCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    if (requestParameters.paymentMethodPreferenceId !== void 0) {
      queryParameters["paymentMethodPreferenceId"] = requestParameters.paymentMethodPreferenceId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences/PaymentMethods/Remove/{checkoutPreferencesId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))).replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentMethodsRemoveCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentMethodsRemoveCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPostRaw(requestParameters, initOverrides) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/AuctionAccess/{auctionAccessId}/CheckoutPreferences`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.createCheckoutPreferencesRequestVersionedCheckoutPreferencesRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPost(requestParameters, initOverrides) {
    const response = await this.apiV2AuctionAccessAuctionAccessIdCheckoutPreferencesPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2CheckoutPreferencesActivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2CheckoutPreferencesActivateCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/CheckoutPreferences/Activate/{checkoutPreferencesId}`.replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2CheckoutPreferencesActivateCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2CheckoutPreferencesActivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2CheckoutPreferencesCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2CheckoutPreferencesCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/CheckoutPreferences/{checkoutPreferencesId}`.replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateCheckoutPreferencesRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2CheckoutPreferencesCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2CheckoutPreferencesCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2CheckoutPreferencesCurrentGetRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.organizationId !== void 0) {
      queryParameters["organizationId"] = requestParameters.organizationId;
    }
    if (requestParameters.auctionAccessId !== void 0) {
      queryParameters["auctionAccessId"] = requestParameters.auctionAccessId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/CheckoutPreferences/Current`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2CheckoutPreferencesCurrentGet(requestParameters = {}, initOverrides) {
    const response = await this.apiV2CheckoutPreferencesCurrentGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2CheckoutPreferencesDeactivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides) {
    if (requestParameters.checkoutPreferencesId === null || requestParameters.checkoutPreferencesId === void 0) {
      throw new RequiredError("checkoutPreferencesId", "Required parameter requestParameters.checkoutPreferencesId was null or undefined when calling apiV2CheckoutPreferencesDeactivateCheckoutPreferencesIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/CheckoutPreferences/Deactivate/{checkoutPreferencesId}`.replace(`{${"checkoutPreferencesId"}}`, encodeURIComponent(String(requestParameters.checkoutPreferencesId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2CheckoutPreferencesDeactivateCheckoutPreferencesIdPut(requestParameters, initOverrides) {
    const response = await this.apiV2CheckoutPreferencesDeactivateCheckoutPreferencesIdPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2CheckoutPreferencesPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/CheckoutPreferences`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.createCheckoutPreferencesRequestVersionedCheckoutPreferencesRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2CheckoutPreferencesPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2CheckoutPreferencesPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class OktaApi extends BaseAPI {
  async apiV2OktaUserProfileChangedGetRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Okta/UserProfileChanged`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OktaUserProfileChangedGet(initOverrides) {
    const response = await this.apiV2OktaUserProfileChangedGetRaw(initOverrides);
    return await response.value();
  }
  async apiV2OktaUserProfileChangedPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Okta/UserProfileChanged`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.oktaHookPayload
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2OktaUserProfileChangedPost(requestParameters = {}, initOverrides) {
    await this.apiV2OktaUserProfileChangedPostRaw(requestParameters, initOverrides);
  }
}

class OrganizationGroupApi extends BaseAPI {
  async apiV2OrganizationGroupOrganizationGroupIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationGroupId === null || requestParameters.organizationGroupId === void 0) {
      throw new RequiredError("organizationGroupId", "Required parameter requestParameters.organizationGroupId was null or undefined when calling apiV2OrganizationGroupOrganizationGroupIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/OrganizationGroup/{organizationGroupId}`.replace(`{${"organizationGroupId"}}`, encodeURIComponent(String(requestParameters.organizationGroupId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationGroupOrganizationGroupIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2OrganizationGroupOrganizationGroupIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2OrganizationGroupPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/OrganizationGroup`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.organizationGroupRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationGroupPost(requestParameters = {}, initOverrides) {
    const response = await this.apiV2OrganizationGroupPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class OrganizationsApi extends BaseAPI {
  async apiV2OrganizationsGetRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.namePrefix !== void 0) {
      queryParameters["namePrefix"] = requestParameters.namePrefix;
    }
    if (requestParameters.enrollment !== void 0) {
      queryParameters["enrollment"] = requestParameters.enrollment;
    }
    if (requestParameters.maxResults !== void 0) {
      queryParameters["maxResults"] = requestParameters.maxResults;
    }
    if (requestParameters.canSell !== void 0) {
      queryParameters["canSell"] = requestParameters.canSell;
    }
    if (requestParameters.orgNameType !== void 0) {
      queryParameters["orgNameType"] = requestParameters.orgNameType;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationsGet(requestParameters = {}, initOverrides) {
    const response = await this.apiV2OrganizationsGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2OrganizationsIdentifierSourceSystemSourceIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.sourceSystem === null || requestParameters.sourceSystem === void 0) {
      throw new RequiredError("sourceSystem", "Required parameter requestParameters.sourceSystem was null or undefined when calling apiV2OrganizationsIdentifierSourceSystemSourceIdGet.");
    }
    if (requestParameters.sourceId === null || requestParameters.sourceId === void 0) {
      throw new RequiredError("sourceId", "Required parameter requestParameters.sourceId was null or undefined when calling apiV2OrganizationsIdentifierSourceSystemSourceIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/Identifier/{sourceSystem}/{sourceId}`.replace(`{${"sourceSystem"}}`, encodeURIComponent(String(requestParameters.sourceSystem))).replace(`{${"sourceId"}}`, encodeURIComponent(String(requestParameters.sourceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationsIdentifierSourceSystemSourceIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2OrganizationsIdentifierSourceSystemSourceIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2OrganizationsOrganizationIdAgreementsGetRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdAgreementsGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}/Agreements`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdAgreementsGet(requestParameters, initOverrides) {
    const response = await this.apiV2OrganizationsOrganizationIdAgreementsGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2OrganizationsOrganizationIdBuySellPermissionsPutRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdBuySellPermissionsPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}/BuySellPermissions`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateOrganizationBuySellIndicatorRequest
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdBuySellPermissionsPut(requestParameters, initOverrides) {
    await this.apiV2OrganizationsOrganizationIdBuySellPermissionsPutRaw(requestParameters, initOverrides);
  }
  async apiV2OrganizationsOrganizationIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.copyPulsarLogic !== void 0) {
      queryParameters["copyPulsarLogic"] = requestParameters.copyPulsarLogic;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2OrganizationsOrganizationIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2OrganizationsOrganizationIdGroupsAddPostRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdGroupsAddPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}/Groups/Add`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.organizationGroupRequest
    }, initOverrides);
    return new TextApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdGroupsAddPost(requestParameters, initOverrides) {
    const response = await this.apiV2OrganizationsOrganizationIdGroupsAddPostRaw(requestParameters, initOverrides);
    return await response.value();
  }
  async apiV2OrganizationsOrganizationIdGroupsOrganizationGroupIdDeleteRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdGroupsOrganizationGroupIdDelete.");
    }
    if (requestParameters.organizationGroupId === null || requestParameters.organizationGroupId === void 0) {
      throw new RequiredError("organizationGroupId", "Required parameter requestParameters.organizationGroupId was null or undefined when calling apiV2OrganizationsOrganizationIdGroupsOrganizationGroupIdDelete.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}/Groups/{organizationGroupId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"organizationGroupId"}}`, encodeURIComponent(String(requestParameters.organizationGroupId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdGroupsOrganizationGroupIdDelete(requestParameters, initOverrides) {
    await this.apiV2OrganizationsOrganizationIdGroupsOrganizationGroupIdDeleteRaw(requestParameters, initOverrides);
  }
  async apiV2OrganizationsOrganizationIdGroupsPutRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdGroupsPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}/Groups`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateOrganizationGroupsRequest
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdGroupsPut(requestParameters, initOverrides) {
    await this.apiV2OrganizationsOrganizationIdGroupsPutRaw(requestParameters, initOverrides);
  }
  async apiV2OrganizationsOrganizationIdUpdateOrganizationGroupPutRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsOrganizationIdUpdateOrganizationGroupPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/{organizationId}/UpdateOrganizationGroup`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateOrganizationGroupRequest
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2OrganizationsOrganizationIdUpdateOrganizationGroupPut(requestParameters, initOverrides) {
    await this.apiV2OrganizationsOrganizationIdUpdateOrganizationGroupPutRaw(requestParameters, initOverrides);
  }
  async apiV2OrganizationsRepsOrganizationIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV2OrganizationsRepsOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.includeInvites !== void 0) {
      queryParameters["includeInvites"] = requestParameters.includeInvites;
    }
    if (requestParameters.filteredRoles) {
      queryParameters["filteredRoles"] = requestParameters.filteredRoles;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Organizations/Reps/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2OrganizationsRepsOrganizationIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2OrganizationsRepsOrganizationIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class PaymentMethodsApi extends BaseAPI {
  async apiV2PaymentMethodsPaymentMethodIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.paymentMethodId === null || requestParameters.paymentMethodId === void 0) {
      throw new RequiredError("paymentMethodId", "Required parameter requestParameters.paymentMethodId was null or undefined when calling apiV2PaymentMethodsPaymentMethodIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/PaymentMethods/{paymentMethodId}`.replace(`{${"paymentMethodId"}}`, encodeURIComponent(String(requestParameters.paymentMethodId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2PaymentMethodsPaymentMethodIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2PaymentMethodsPaymentMethodIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class RegisterApi extends BaseAPI {
  async apiV2RegisterPutRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (requestParameters.acceptLanguage !== void 0 && requestParameters.acceptLanguage !== null) {
      headerParameters["Accept-Language"] = String(requestParameters.acceptLanguage);
    }
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Register`,
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.registerRepRequest
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2RegisterPut(requestParameters = {}, initOverrides) {
    const response = await this.apiV2RegisterPutRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class RepsApi extends BaseAPI {
  async apiV2RepsAgreementsPutRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Reps/Agreements`,
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repUpdateAgreementsRequest
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2RepsAgreementsPut(requestParameters = {}, initOverrides) {
    await this.apiV2RepsAgreementsPutRaw(requestParameters, initOverrides);
  }
  async apiV2RepsRepIdAgreementsPutRaw(requestParameters, initOverrides) {
    if (requestParameters.repId === null || requestParameters.repId === void 0) {
      throw new RequiredError("repId", "Required parameter requestParameters.repId was null or undefined when calling apiV2RepsRepIdAgreementsPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Reps/{repId}/Agreements`.replace(`{${"repId"}}`, encodeURIComponent(String(requestParameters.repId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repUpdateAgreementsRequest
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2RepsRepIdAgreementsPut(requestParameters, initOverrides) {
    await this.apiV2RepsRepIdAgreementsPutRaw(requestParameters, initOverrides);
  }
  async apiV2RepsRepIdGetRaw(requestParameters, initOverrides) {
    if (requestParameters.repId === null || requestParameters.repId === void 0) {
      throw new RequiredError("repId", "Required parameter requestParameters.repId was null or undefined when calling apiV2RepsRepIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.copyPulsarLogic !== void 0) {
      queryParameters["copyPulsarLogic"] = requestParameters.copyPulsarLogic;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/Reps/{repId}`.replace(`{${"repId"}}`, encodeURIComponent(String(requestParameters.repId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response);
  }
  async apiV2RepsRepIdGet(requestParameters, initOverrides) {
    const response = await this.apiV2RepsRepIdGetRaw(requestParameters, initOverrides);
    return await response.value();
  }
}

class SourceIdApi extends BaseAPI {
  async apiV2SourceIdPostRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", []);
    }
    const response = await this.request({
      path: `/api/v2/SourceId`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.sourceIdRequest
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  async apiV2SourceIdPost(requestParameters = {}, initOverrides) {
    await this.apiV2SourceIdPostRaw(requestParameters, initOverrides);
  }
}

var AddressDefaultTypes;
(function(AddressDefaultTypes2) {
  AddressDefaultTypes2["Transportation"] = "Transportation";
  AddressDefaultTypes2["Title"] = "Title";
  AddressDefaultTypes2["Billing"] = "Billing";
  AddressDefaultTypes2["Mailing"] = "Mailing";
})(AddressDefaultTypes || (AddressDefaultTypes = {}));
var AddressType;
(function(AddressType2) {
  AddressType2["Lot"] = "Lot";
  AddressType2["Billing"] = "Billing";
  AddressType2["Mailing"] = "Mailing";
  AddressType2["Payments"] = "Payments";
  AddressType2["Home"] = "Home";
  AddressType2["Physical"] = "Physical";
  AddressType2["BankReferenceLetter"] = "BankReferenceLetter";
  AddressType2["Grounding"] = "Grounding";
  AddressType2["Street"] = "Street";
  AddressType2["Office"] = "Office";
  AddressType2["Corporate"] = "Corporate";
  AddressType2["BankDraft"] = "BankDraft";
  AddressType2["Invoices"] = "Invoices";
  AddressType2["Arbitration"] = "Arbitration";
  AddressType2["Inquires"] = "Inquires";
})(AddressType || (AddressType = {}));
var Agreement;
(function(Agreement2) {
  Agreement2["TermsAndConditions"] = "TermsAndConditions";
  Agreement2["PrivacyPolicy"] = "PrivacyPolicy";
})(Agreement || (Agreement = {}));
var AutomaticCheckoutActiveStatus;
(function(AutomaticCheckoutActiveStatus2) {
  AutomaticCheckoutActiveStatus2["Active"] = "Active";
  AutomaticCheckoutActiveStatus2["Inactive"] = "Inactive";
})(AutomaticCheckoutActiveStatus || (AutomaticCheckoutActiveStatus = {}));
var AutomaticCheckoutStatus;
(function(AutomaticCheckoutStatus2) {
  AutomaticCheckoutStatus2["Active"] = "Active";
  AutomaticCheckoutStatus2["Inactive"] = "Inactive";
  AutomaticCheckoutStatus2["Deprecated"] = "Deprecated";
  AutomaticCheckoutStatus2["Delinquent"] = "Delinquent";
})(AutomaticCheckoutStatus || (AutomaticCheckoutStatus = {}));
var BusinessType;
(function(BusinessType2) {
  BusinessType2["Independent"] = "Independent";
  BusinessType2["Franchise"] = "Franchise";
})(BusinessType || (BusinessType = {}));
var BusinessUnit;
(function(BusinessUnit2) {
  BusinessUnit2["None"] = "None";
  BusinessUnit2["TradeRev"] = "TradeRev";
  BusinessUnit2["AdesaCa"] = "AdesaCa";
  BusinessUnit2["AdesaCom"] = "AdesaCom";
  BusinessUnit2["Adesa"] = "Adesa";
})(BusinessUnit || (BusinessUnit = {}));
var Features;
(function(Features2) {
  Features2["Marketguide"] = "Marketguide";
  Features2["MarketplaceCaHard"] = "MarketplaceCaHard";
})(Features || (Features = {}));
var IssueName;
(function(IssueName2) {
  IssueName2["AdesaDocumentExpired"] = "ADESADocumentExpired";
  IssueName2["AdesaDocumentNotScanned"] = "ADESADocumentNotScanned";
  IssueName2["AdesaDocumentRejected"] = "ADESADocumentRejected";
  IssueName2["AdminHoldBuy"] = "AdminHoldBuy";
  IssueName2["AdminHoldSell"] = "AdminHoldSell";
  IssueName2["ApplicationNotScanned"] = "ApplicationNotScanned";
  IssueName2["ApplicationRejected"] = "ApplicationRejected";
  IssueName2["AutoTecRequestedDocument"] = "AutoTecRequestedDocument";
  IssueName2["DealerAuthorizationLetterNotScanned"] = "DealerAuthorizationLetterNotScanned";
  IssueName2["DealerAuthorizationLetterRejected"] = "DealerAuthorizationLetterRejected";
  IssueName2["Duplicate"] = "Duplicate";
  IssueName2["FinancialHoldBuy"] = "FinancialHoldBuy";
  IssueName2["FinancialHoldSell"] = "FinancialHoldSell";
  IssueName2["GroupUnauthorizedBuy"] = "GroupUnauthorizedBuy";
  IssueName2["GroupUnauthorizedSell"] = "GroupUnauthorizedSell";
  IssueName2["IdVerificationWarning"] = "IDVerificationWarning";
  IssueName2["InRedFlagsViolation"] = "InRedFlagsViolation";
  IssueName2["InViolationofAutoTecPolicies"] = "InViolationofAutoTecPolicies";
  IssueName2["IndividualAuthorizationRejected"] = "IndividualAuthorizationRejected";
  IssueName2["IndividualAuthorizationNotScanned"] = "IndividualAuthorizationNotScanned";
  IssueName2["InternationalDocumentationNotVerifiable"] = "InternationalDocumentationNotVerifiable";
  IssueName2["InvalidInformation"] = "InvalidInformation";
  IssueName2["KoBook"] = "KOBook";
  IssueName2["LicenseExpired"] = "LicenseExpired";
  IssueName2["LicenseNotScanned"] = "LicenseNotScanned";
  IssueName2["LicenseRejected"] = "LicenseRejected";
  IssueName2["LicenseRevokedCancelledSuspended"] = "LicenseRevokedCancelledSuspended";
  IssueName2["LockedperOwnerOfficer"] = "LockedperOwnerOfficer";
  IssueName2["ManheimDocumentExpired"] = "ManheimDocumentExpired";
  IssueName2["ManheimDocumentNotScanned"] = "ManheimDocumentNotScanned";
  IssueName2["ManheimDocumentRejected"] = "ManheimDocumentRejected";
  IssueName2["MembershipExpired"] = "MembershipExpired";
  IssueName2["MembershipFeeDue"] = "MembershipFeeDue";
  IssueName2["MembershipPolicyViolation"] = "MembershipPolicyViolation";
  IssueName2["MissingGovernmentId"] = "MissingGovernmentID";
  IssueName2["MissingRequiredData"] = "MissingRequiredData";
  IssueName2["OwnerinKoBook"] = "OwnerinKOBook";
  IssueName2["PersonalLicenseExpired"] = "PersonalLicenseExpired";
  IssueName2["PersonalLicenseNotScanned"] = "PersonalLicenseNotScanned";
  IssueName2["PersonalLicenseRejected"] = "PersonalLicenseRejected";
  IssueName2["PhotoIdExpired"] = "PhotoIDExpired";
  IssueName2["PhotoIdNotScanned"] = "PhotoIDNotScanned";
  IssueName2["PhotoIdRejected"] = "PhotoIDRejected";
  IssueName2["RefusestobeinAuctionAccess"] = "RefusestobeinAuctionACCESS";
  IssueName2["RepresentativeinKoBook"] = "RepresentativeinKOBook";
  IssueName2["RequiredDocumentExpired"] = "RequiredDocumentExpired";
  IssueName2["RequiredDocumentNotScanned"] = "RequiredDocumentNotScanned";
  IssueName2["RequiredDocumentRejected"] = "RequiredDocumentRejected";
  IssueName2["RoleReinstatedwithoutPaperwork"] = "RoleReinstatedwithoutPaperwork";
  IssueName2["UnderAutotecReview"] = "UnderAutotecReview";
})(IssueName || (IssueName = {}));
var IssueRequestEntityType;
(function(IssueRequestEntityType2) {
  IssueRequestEntityType2["Dealership"] = "Dealership";
  IssueRequestEntityType2["Transporter"] = "Transporter";
  IssueRequestEntityType2["CommercialAccount"] = "CommercialAccount";
  IssueRequestEntityType2["CompanyAssociation"] = "CompanyAssociation";
  IssueRequestEntityType2["Person"] = "Person";
  IssueRequestEntityType2["Bank"] = "Bank";
  IssueRequestEntityType2["BankAccount"] = "BankAccount";
  IssueRequestEntityType2["ThirdPartyRemarketing"] = "ThirdPartyRemarketing";
  IssueRequestEntityType2["CustomerCustomization"] = "CustomerCustomization";
})(IssueRequestEntityType || (IssueRequestEntityType = {}));
var IssueVisibility;
(function(IssueVisibility2) {
  IssueVisibility2["Customer"] = "Customer";
  IssueVisibility2["Global"] = "Global";
  IssueVisibility2["Group"] = "Group";
})(IssueVisibility || (IssueVisibility = {}));
var MessageName;
(function(MessageName2) {
  MessageName2["Bnk"] = "BNK";
  MessageName2["Bai"] = "BAI";
  MessageName2["Cac"] = "CAC";
  MessageName2["Cas"] = "CAS";
  MessageName2["Cba"] = "CBA";
  MessageName2["Cci"] = "CCI";
  MessageName2["Cfr"] = "CFR";
  MessageName2["Com"] = "COM";
  MessageName2["Con"] = "CON";
  MessageName2["Ctr"] = "CTR";
  MessageName2["Fpc"] = "FPC";
  MessageName2["Idf"] = "IDF";
  MessageName2["Iss"] = "ISS";
  MessageName2["Par"] = "PAR";
  MessageName2["Pas"] = "PAS";
  MessageName2["Per"] = "PER";
  MessageName2["Prl"] = "PRL";
})(MessageName || (MessageName = {}));
var MileageType;
(function(MileageType2) {
  MileageType2["Actual"] = "Actual";
  MileageType2["Tmu"] = "Tmu";
  MileageType2["Exceeds"] = "Exceeds";
  MileageType2["Exempt"] = "Exempt";
})(MileageType || (MileageType = {}));
var MileageUnit;
(function(MileageUnit2) {
  MileageUnit2["Miles"] = "Miles";
  MileageUnit2["Kilometers"] = "Kilometers";
  MileageUnit2["Hours"] = "Hours";
})(MileageUnit || (MileageUnit = {}));
var ObjectType;
(function(ObjectType2) {
  ObjectType2["Address"] = "Address";
  ObjectType2["FinancingAccount"] = "FinancingAccount";
  ObjectType2["Organization"] = "Organization";
  ObjectType2["OrganizationGroup"] = "OrganizationGroup";
  ObjectType2["PaymentMethod"] = "PaymentMethod";
  ObjectType2["Rep"] = "Rep";
  ObjectType2["CreditCard"] = "CreditCard";
  ObjectType2["Registration"] = "Registration";
})(ObjectType || (ObjectType = {}));
var OrgNameType;
(function(OrgNameType2) {
  OrgNameType2["DisplayName"] = "DisplayName";
  OrgNameType2["SellAs"] = "SellAs";
})(OrgNameType || (OrgNameType = {}));
var OverrideLevel;
(function(OverrideLevel2) {
  OverrideLevel2["Auction"] = "Auction";
  OverrideLevel2["Group"] = "Group";
})(OverrideLevel || (OverrideLevel = {}));
var PaymentAccountStatus;
(function(PaymentAccountStatus2) {
  PaymentAccountStatus2["Validating"] = "Validating";
  PaymentAccountStatus2["Valid"] = "Valid";
  PaymentAccountStatus2["Expiring"] = "Expiring";
  PaymentAccountStatus2["Error"] = "Error";
  PaymentAccountStatus2["Revoked"] = "Revoked";
  PaymentAccountStatus2["Closed"] = "Closed";
  PaymentAccountStatus2["NotValid"] = "NotValid";
  PaymentAccountStatus2["Expired"] = "Expired";
})(PaymentAccountStatus || (PaymentAccountStatus = {}));
var PaymentMethodType;
(function(PaymentMethodType2) {
  PaymentMethodType2["EftAch"] = "EftAch";
  PaymentMethodType2["Financing"] = "Financing";
})(PaymentMethodType || (PaymentMethodType = {}));
var ProgramEnrollment;
(function(ProgramEnrollment2) {
  ProgramEnrollment2["Ca"] = "OMP_CA";
  ProgramEnrollment2["Us"] = "OMP_US";
})(ProgramEnrollment || (ProgramEnrollment = {}));
var PurchaseType;
(function(PurchaseType2) {
  PurchaseType2["WholeCarAuction"] = "WholeCarAuction";
  PurchaseType2["Salvage"] = "Salvage";
  PurchaseType2["Float"] = "Float";
})(PurchaseType || (PurchaseType = {}));
var Region;
(function(Region2) {
  Region2["Gl"] = "GL";
  Region2["Ca"] = "CA";
  Region2["Us"] = "US";
})(Region || (Region = {}));
var RepType;
(function(RepType2) {
  RepType2["Customer"] = "Customer";
  RepType2["Employee"] = "Employee";
})(RepType || (RepType = {}));
var Role;
(function(Role2) {
  Role2["Buyer"] = "Buyer";
  Role2["Seller"] = "Seller";
  Role2["Admin"] = "Admin";
  Role2["Accountant"] = "Accountant";
  Role2["Owner"] = "Owner";
  Role2["Bidder"] = "Bidder";
  Role2["ViewOnly"] = "ViewOnly";
  Role2["InventoryCreator"] = "InventoryCreator";
})(Role || (Role = {}));
var SourceSystem;
(function(SourceSystem2) {
  SourceSystem2["Auctionaccess"] = "AUCTIONACCESS";
  SourceSystem2["Plaid"] = "PLAID";
  SourceSystem2["Traderev"] = "TRADEREV";
  SourceSystem2["Openlane"] = "OPENLANE";
  SourceSystem2["Physical"] = "PHYSICAL";
  SourceSystem2["Test"] = "TEST";
  SourceSystem2["Mdm"] = "MDM";
  SourceSystem2["Ec"] = "EC";
  SourceSystem2["EReg"] = "EReg";
  SourceSystem2["Amscustomer"] = "AMSCUSTOMER";
  SourceSystem2["Backlot"] = "BACKLOT";
  SourceSystem2["Afc"] = "AFC";
  SourceSystem2["Salesforce"] = "SALESFORCE";
  SourceSystem2["GenericReadonly"] = "GENERIC_READONLY";
  SourceSystem2["Omp"] = "OMP";
})(SourceSystem || (SourceSystem = {}));
var TaxCode;
(function(TaxCode2) {
  TaxCode2["Hst"] = "HST";
  TaxCode2["Qst"] = "QST";
  TaxCode2["Pst"] = "PST";
  TaxCode2["Rst"] = "RST";
  TaxCode2["Gst"] = "GST";
  TaxCode2["Federal"] = "FEDERAL";
  TaxCode2["State"] = "STATE";
})(TaxCode || (TaxCode = {}));
var YesNo;
(function(YesNo2) {
  YesNo2["Y"] = "Y";
  YesNo2["N"] = "N";
})(YesNo || (YesNo = {}));

export { AddressDefaultTypes, AddressType, AddressesApi, Agreement, AuctionAccessApi, AuditsApi, AuthorizationApi, AutomaticCheckoutActiveStatus, AutomaticCheckoutStatus, BASE_PATH, BaseAPI, BlobApiResponse, BusinessType, BusinessUnit, COLLECTION_FORMATS, CheckoutPreferencesApi, Configuration, Features, IssueName, IssueRequestEntityType, IssueVisibility, JSONApiResponse, MessageName, MileageType, MileageUnit, ObjectType, OktaApi, OrgNameType, OrganizationGroupApi, OrganizationsApi, OverrideLevel, PaymentAccountStatus, PaymentMethodType, PaymentMethodsApi, ProgramEnrollment, PurchaseType, Region, RegisterApi, RepType, RepsApi, RequiredError, Role, SourceIdApi, SourceSystem, TaxCode, TextApiResponse, VoidApiResponse, YesNo, canConsumeForm, exists, mapValues, querystring };
